import { Link } from 'react-router-dom';

import { OverflowFadeText } from '@/components/layout/OverflowFadeText';
import { BREADCRUMB_ROOT_CLASS } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * The workbench's identity line — the object you are editing, and nothing else.
 *
 * ── THE RULE, WHICH IS NOT SPECIFIC TO THIS PAGE ──────────────────────────
 * **Tool-chrome headers show the object's own identity. They never spell out
 * the ancestor chain as text.** Ancestor navigation lives in two places that
 * already exist and cost no header width: the back arrow, which really does go
 * one level up, and an explicit "Open in <ancestor>" row in the `⋯` menu for
 * jumping further. A `PageHeader` on a normal route is a different surface and
 * keeps its breadcrumb; this is `TOOLBAR-DOCTRINE.md` chrome.
 *
 * This file used to render FOUR levels — Briefs / <brief name> / <concept
 * hook> / Design — inside the same 48px bar as a status badge, a quality chip,
 * an autosave line and five icon buttons. Founder, 2026-08-21: _"I don't know
 * if we really need the full breadcrumb there… way too deep. I don't think we
 * should ever have four nested breadcrumbs."_
 *
 * What each dropped level cost, and where it went:
 *
 * - **"Design"** duplicated the `Design | Preview` toggle that sits visible on
 *   the canvas on every render, eleven characters below it. A trailing
 *   mode-label crumb is always redundant wherever a visible tab or toggle
 *   already states the mode — check for that before adding one.
 * - **The brief name** and **"Briefs"** are two clicks the marketer can still
 *   make: `⋯ → Open in brief` already existed before this change. The
 *   capability was not deleted, it moved out of the busiest row in the app.
 * - **The concept hook stays**, because it is the one segment that answers
 *   "which of these five concepts am I editing" — and during load, or between
 *   two size variants that look alike, the canvas cannot answer it.
 *
 * ── WHY THIS GENERALISES ──────────────────────────────────────────────────
 * The same shape recurs wherever Mora nests objects — Campaign › Ad group ›
 * Ad is the next one. Spelling that chain out produces the identical mess.
 * Applied there: the back arrow returns to the ad group, the header names the
 * AD, and "Open in campaign" is a menu row. The depth of the data model is not
 * a reason to render its depth in chrome.
 *
 * Deliberately NOT `InspectorBreadcrumb`: that primitive renders only the
 * first and last of the crumbs it is given and silently drops the middle
 * (design-system hard rule 27). With one crumb there is nothing to drop, but
 * this is a plain link rather than a crumb list precisely because a
 * single-item breadcrumb is not a breadcrumb.
 */
export interface ConceptDesignBreadcrumbProps {
  briefId: string;
  projectTaskId: string;
  /** Retained for the caller's convenience; not rendered. See the header. */
  briefName: string;
  conceptLeaf: string;
}

export function ConceptDesignBreadcrumb({
  briefId,
  projectTaskId,
  conceptLeaf,
}: ConceptDesignBreadcrumbProps): JSX.Element {
  return (
    <div className="flex min-w-0 flex-1 items-center">
      <Link
        to={`/briefs/${briefId}/concepts/${projectTaskId}`}
        className={cn(BREADCRUMB_ROOT_CLASS, 'block min-w-0 text-sm font-medium tracking-tight')}
      >
        <OverflowFadeText className="block min-w-0 max-w-48 lg:max-w-md" title={conceptLeaf}>
          {conceptLeaf}
        </OverflowFadeText>
      </Link>
    </div>
  );
}
